// The company bank account a payment goes through. Every module that moves money asks with this,
// so "which bank?" reads the same on an invoice, a loan instalment, a pay run and an asset sale —
// and the G/L account the cash lands on is visible before anything posts.
import { type BankAccount, useApi } from "@keel/core";
import { useEffect } from "react";
import { Select } from "./Select";

/**
 * Active bank and cash accounts, from `path`. Finance's own list needs a finance permission, so a
 * module that pays without one passes its own endpoint (e.g. `/loans/bank-accounts`) answering
 * the same shape.
 */
export function useBankAccounts(path = "/bank-accounts") {
  const q = useApi<BankAccount[]>(path);
  return { ...q, data: q.data?.filter((b) => b.isActive) };
}

/** "1010 · Swedbank · G/L 1010" — what the account is, and where its money is booked. */
export const bankLabel = (b: BankAccount) => `${b.code} · ${b.name} · G/L ${b.glAccount}`;

export function BankAccountSelect({
  value,
  onChange,
  path,
  preferred,
}: {
  value: string;
  onChange: (id: string, account?: BankAccount) => void;
  /** Where to read the list from; defaults to finance's `/bank-accounts`. */
  path?: string;
  /** Chosen when nothing is yet: e.g. the bank named on a loan agreement. */
  preferred?: string | null;
}) {
  const accounts = useBankAccounts(path).data;
  // Start on the account the document already names, else the company's default — the choice is
  // still shown, and still the person's to change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: fills an empty choice once the list arrives
  useEffect(() => {
    if (value || !accounts?.length) return;
    const pick = accounts.find((b) => b.id === preferred) ?? accounts.find((b) => b.isDefault) ?? accounts[0];
    if (pick) onChange(pick.id, pick);
  }, [accounts, preferred]);
  if (accounts && accounts.length === 0)
    return <span className="muted">No bank account yet — add one under Settings → Bank accounts.</span>;
  return (
    <Select
      value={value}
      placeholder="Choose a bank account…"
      onChange={(e) =>
        onChange(
          e.target.value,
          accounts?.find((b) => b.id === e.target.value),
        )
      }
    >
      {(accounts ?? []).map((b) => (
        <option key={b.id} value={b.id}>
          {bankLabel(b)}
        </option>
      ))}
    </Select>
  );
}
